import * as SharedAppState from '@AppModels/AppState';
import * as Api from '@ViewModels';
import { IConfirmationDialogOption } from '../../web/components/ConfirmationDialog';
import { reactQueryClient } from '../../web/ReactQueryProvider';
import { EventLogger } from '../Logging';
import { abortAllAIStreamsAndClear, resetConversationDrawerState } from '../../web/containers/templates/AssistantChat';

/**
 * Delete confirmation options
 * @source Original location: UiUtils.ts.backup line 79
 */
export const DeleteConfirmationOptions: IConfirmationDialogOption[] = [
	{
		isDestructive: true,
		representedObject: true,
		title: 'Delete',
	},
	{
		isCancel: true,
		representedObject: false,
		title: 'Cancel',
	},
];

/**
 * Debounce function
 * @source Original location: UiUtils.ts.backup line 128
 */
export const debounce = <T extends unknown[]>(func: (...args: T) => void, wait: number) => {
	let timeout: number;

	const debounced = function (...args: T) {
		clearTimeout(timeout);
		timeout = window.setTimeout(() => {
			timeout = null;
			func(...args);
		}, wait);
	};

	debounced.cancel = () => {
		clearTimeout(timeout);
		timeout = null;
	};

	return debounced;
};

/**
 * Opens download URL
 * @source Original location: UiUtils.ts.backup line 1017
 */
export const openDownloadUrl = (url: string, fileName: string) => {
	if (url) {
		const link = document.createElement('a');
		link.setAttribute('style', 'display:none;opacity=0;position:absolute;visibility=collapse;');
		link.href = url;
		link.download = fileName;
		link.target = '_blank';
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	}
};

/**
 * Opens URL in new tab
 * @source Original location: UiUtils.ts.backup line 1030
 */
export const openUrlInNewTab = (url: string) => {
	if (url) {
		const link = document.createElement('a');
		link.setAttribute('style', 'display:none;opacity=0;position:absolute;visibility=collapse;');
		link.href = url;
		link.target = '_blank';
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	}
};

export const copyToClipboard = async (value: string) => {
	try {
		await navigator.clipboard.writeText(value);
		return true;
	} catch (e) {
		EventLogger.logEvent(
			{
				action: 'CopyToClipboard',
				category: 'UiUtils',
			},
			{ exception: Api.asApiError(e) }
		);
		return false;
	}
};

export const nodeListToArray = <T extends HTMLElement = HTMLElement>(nodes?: NodeList) => {
	if (nodes) {
		return Array.prototype.slice.call(nodes) as T[];
	}

	return [] as T[];
};

export const dataAttributes = (data?: Record<string, string>) => {
	if (data) {
		return Object.keys(data).reduce<Record<string, string>>((res, x) => {
			let key = (x || '').trim();
			if (key) {
				const dataKey = key;
				/**
				 * If the key does not start with 'data-', add it
				 */
				if (!key.startsWith('data-')) {
					key = `data-${key}`;
				}
				res[key] = data[dataKey];
			}
			return res;
		}, {});
	}
	return {};
};

/**
 * Returns data attributes for dev mode element debugging
 * @note Type safety improvement: Changed from Record<string, any> to Record<string, unknown>
 */
export const devElementDataProps = (data?: Record<string, unknown>) => {
	if (!import.meta.env.PROD && Object.keys(data || {}).length > 0) {
		// Type assertion is safe here because dataAttributes expects Record<string, string>
		// and we're only using this in dev mode for debugging attributes
		return dataAttributes(data as Record<string, string>);
	}
	return {};
};

export const removeForbiddenTagsFromHTML = (html: string) => {
	const parser = new DOMParser();
	const parsedDocument = parser.parseFromString(html?.trim(), 'text/html');

	const forbiddenTags = ['iframe'];

	let errors = {};
	for (const forbiddenTag of forbiddenTags) {
		const forbiddenTagNodes = parsedDocument.querySelectorAll(forbiddenTag);

		if (forbiddenTagNodes.length > 0) {
			errors = { ...errors, [forbiddenTag]: true };
		}

		for (const node of forbiddenTagNodes) {
			node.remove();
		}
	}

	return { html: parsedDocument.documentElement.outerHTML.trim(), errors };
};

export const tinymceEditorPlaceholderStyles = `
span[data-placeholder="true"] {
	border-radius: 3px;
	border: 1px dashed #00AAE8;
	box-sizing: border-box;
	padding: 1px 5px;
}

span[data-placeholder="true"][contentEditable=false] {
	cursor: pointer;
}

span[data-placeholder="true"][contentEditable=false][data-mce-selected] {
	cursor: pointer;
	outline: none;
}`;

export const unlayerCustomCss = `span[data-placeholder="true"] {
	border-radius: 3px;
	border: 1px dashed #00AAE8;
	box-sizing: border-box;
	padding: 1px 5px;
}

span[data-placeholder="true"][contentEditable=false] {
	cursor: pointer;
}

span[data-placeholder="true"][contentEditable=false][data-mce-selected] {
	cursor: pointer;
	outline: none;
}

/* Remove the image editor button */
.blockbuilder-image-widget.row .blockbuilder-widget-label button.btn.btn-secondary {
	border: none !important;
	display: none !important;
	height: 0 !important;
	margin: 0 !important;
	opacity: 0 !important;
	padding: 0 !important;
	position: fixed !important;
	width: 0 !important;
}
`;

// TinyMCE Toolbar Constants
export const TinyMCEUndoRedo = 'undo redo';
export const TinyMCEToolbarBoldItalicUnderline = ` bold italic underline `;
export const TinyMCEToolbarBase = ` ${TinyMCEUndoRedo} | ${TinyMCEToolbarBoldItalicUnderline} `;
export const TinyMCEToolbarFonts = ' fontsizeselect | fontselect ';
export const TinyMCEToolbarColor = ' forecolor ';
export const TinyMCEToolbarHyperlink = ' link ';
export const TinyMCEToolbarClearFormatting = ' removeformat ';
export const TinyMCEToolbarInsert = ' insertmenu ';
export const TinyMCESignatureToolbarInsert = ' insertsigmenu ';
export const TinyMCEToolbarTable = ' table ';
export const TinyMCEToolbarList = ' listmenu ';
export const TinyMCEToolbarAlign = ' alignmentmenu ';
export const TinyMCEToolbarMerge = ' mergefieldmenu ';
export const TinyMCEToolbarSignatureMerge = ' signaturemergefieldmenu ';
export const ToolbarAIDA = `${TinyMCEToolbarBoldItalicUnderline} | fontsizeselect | ${TinyMCEToolbarColor} | ${TinyMCEToolbarList} | link | emoticons`;
export const ToolbarDefault = `${TinyMCEToolbarBoldItalicUnderline} | fontsizeselect | ${TinyMCEToolbarColor} | ${TinyMCEToolbarList} | link insert-image`;
export const ToolbarLight = `${TinyMCEToolbarBoldItalicUnderline} | ${TinyMCEToolbarList} | link`;
export const ToolbarUnsubscribe = `${TinyMCEToolbarBase} | fontsizeselect | ${TinyMCEToolbarColor}`;
export const ToolbarNotes = `${TinyMCEToolbarBoldItalicUnderline} | ${TinyMCEToolbarList} link insert-image`;
export const ToolbarAdminNotes = `${TinyMCEToolbarBase} | fontsizeselect | ${TinyMCEToolbarColor} ${TinyMCEToolbarList} | link | file-attachments`;
export const ToolbarFull = `${TinyMCEToolbarBase} | ${TinyMCEToolbarColor} | ${TinyMCEToolbarFonts} | ${TinyMCEToolbarInsert} | ${TinyMCEToolbarTable} | ${TinyMCEToolbarList} | ${TinyMCEToolbarAlign} | ${TinyMCEToolbarMerge}|emoticons`;
export const ToolbarSmall = `${TinyMCEToolbarBoldItalicUnderline} | ${TinyMCEToolbarColor} | ${TinyMCEToolbarFonts} | ${TinyMCEToolbarList} | link`;
export const ToolbarBlog = `${TinyMCEToolbarBoldItalicUnderline} | h1 h2 h3 h4 h5 h6 | listmenu table | link insert-image insert-video`;
export const ToolbarCustomSurvey = `${TinyMCEToolbarBoldItalicUnderline} | ${TinyMCEToolbarColor} | link | ${TinyMCEToolbarClearFormatting}`;
export const ToolbarSignature = `${TinyMCEToolbarBase} | ${TinyMCEToolbarColor} | ${TinyMCEToolbarFonts} | ${TinyMCESignatureToolbarInsert} | ${TinyMCEToolbarTable} | ${TinyMCEToolbarList} | ${TinyMCEToolbarAlign}`;
// `ToolbarSignature` is also the editor-wide default toolbar, so the signature merge field menu gets
// its own constant rather than being folded in — only the signature editor should offer it.
export const ToolbarSignatureWithMergeFields = `${ToolbarSignature} | ${TinyMCEToolbarSignatureMerge}`;

export const addOutsideClickListener = (nodes: HTMLElement[], eventTypes: string[], onOutside: (e: Event) => void) => {
	const handler = (e: Event) => {
		if (!nodes.some(node => node.contains(e.target as Node))) {
			onOutside(e);
		}
	};
	eventTypes.forEach(type => document.addEventListener(type, handler));
	return () => eventTypes.forEach(type => document.removeEventListener(type, handler));
};

/**
 * Filters a list into matched and unmatched items based on a condition
 * @note Type safety improvement: Changed from any[] to T[] with generic for better type inference
 */
export const filterList = <T = unknown>(items: T[], condition: (x: T) => boolean) => {
	const matched: T[] = [];
	const unmatched: T[] = [];
	items.forEach(x => {
		const conditionTrue = condition(x);
		if (conditionTrue) {
			matched.push(x);
		} else {
			unmatched.push(x);
		}
	});
	return { matched, unmatched };
};

/**
 * Sets a property value on an object dynamically
 * @note Type safety improvement: Changed from any to generic T for better type inference
 */
export const setObjectPropertyValue = <T extends Record<string, unknown>>(
	object: T,
	propertyName: string,
	value: unknown,
	setOnlyIfObjectHasProperty = true
) => {
	if (!!object && !!propertyName) {
		if (!!setOnlyIfObjectHasProperty && !Object.prototype.hasOwnProperty.call(object, propertyName)) {
			return;
		}
		// Type assertion is safe here because we're dynamically setting a property
		// that exists on the object type
		(object as Record<string, unknown>)[propertyName] = value;
	}
};

export const getFileSizeStringValue = (numberOfBytes: number) => {
	if (numberOfBytes < 1024) {
		return `${numberOfBytes} bytes`;
	} else if (numberOfBytes >= 1024 && numberOfBytes < 1048576) {
		return `${(numberOfBytes / 1024).toFixed(1)} KB`;
	} else if (numberOfBytes >= 1048576 && numberOfBytes < 1073741824) {
		return `${(numberOfBytes / 1048576).toFixed(1)} MB`;
	}

	return `${(numberOfBytes / 1073741824).toFixed(1)} GB`;
};

export const logObject = (name: string, object: Record<string, unknown>) => {
	console.log(name, JSON.stringify(object, undefined, 5));
};

export type NormalizedNoteVisibility = 'Private' | 'Shared' | 'Not available' | 'Group';

export const getNormalizedNoteVisibility = (visibility: string): NormalizedNoteVisibility => {
	const noteVisibility = visibility || 'none';
	switch (noteVisibility) {
		case 'admin':
			return 'Private';
		case 'all':
			return 'Shared';
		case 'none':
			return 'Not available';
		default:
			return 'Group';
	}
};

export function logoutAndClearCaches() {
	SharedAppState.AppState[SharedAppState.UserSessionViewModelKey].logout();
	SharedAppState.AppState[SharedAppState.TextMessagingViewModelKey]?.fullReset();
	abortAllAIStreamsAndClear();
	// Logging out is a client-side redirect, not a reload, so module state survives it.
	resetConversationDrawerState();
	reactQueryClient.removeQueries();
}
